import React from 'react';
import { AlertCircle, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGeneratedFileDownload } from '@CustomHooks';
import { AgentFileSource, GeneratedFile } from '@Models';
import FileCard from './FileCard';
import { fileCardVariants } from './variants';

interface FileDownloadButtonProps {
  file: GeneratedFile;
  conversationId: string;
  timestamp: string;
  /** `attachment` downloads a file the user sent with the message. */
  source?: AgentFileSource;
  /** The file is still uploading, so there is nothing to download yet. */
  uploading?: boolean;
  /** Position in a sibling list — drives the staggered fade-up animation. */
  index?: number;
  /** Extra seconds added so the cascade can chain after sibling elements (e.g. option cards). */
  staggerOffset?: number;
}

const styles = fileCardVariants();

const FileDownloadButton: React.FC<FileDownloadButtonProps> = ({
  file,
  conversationId,
  timestamp,
  source = 'generated',
  uploading = false,
  index = 0,
  staggerOffset = 0,
}) => {
  const { t } = useTranslation();
  const { download, isPending, error } = useGeneratedFileDownload(conversationId, timestamp, file, source);

  return (
    <div className={styles.container()} style={{ animationDelay: `${index * 0.08 + 0.05 + staggerOffset}s` }}>
      <FileCard
        name={file.file_name}
        mimeType={file.file_type}
        sizeBytes={file.file_size}
        onClick={download}
        pending={isPending || uploading}
        pendingLabel={uploading ? t('Agent.AttachmentSending') : undefined}
        action={<Download className={styles.actionIcon()} />}
      />

      {error && (
        <div className={styles.errorContainer()}>
          <AlertCircle className={styles.errorIcon()} />
          <span>{t('Agent.FileDownloadError')}</span>
        </div>
      )}
    </div>
  );
};

export default FileDownloadButton;
